"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  X,
} from "lucide-react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  show: (message: string, type?: ToastType, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  remove: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, type: ToastType = "info", duration = 4000) => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [...prev, { id, message, type, duration }]);
    },
    []
  );

  const success = useCallback(
    (message: string, duration?: number) => show(message, "success", duration),
    [show]
  );
  const error = useCallback(
    (message: string, duration?: number) => show(message, "error", duration),
    [show]
  );
  const warning = useCallback(
    (message: string, duration?: number) => show(message, "warning", duration),
    [show]
  );
  const info = useCallback(
    (message: string, duration?: number) => show(message, "info", duration),
    [show]
  );

  return (
    <ToastContext.Provider
      value={{ show, success, error, warning, info, remove }}
    >
      {children}
      <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none px-4">
        {toasts.map((t) => (
          <ToastItemView key={t.id} item={t} onRemove={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast debe ser utilizado dentro de un <ToastProvider>");
  }
  return context;
};

const ToastItemView = ({
  item,
  onRemove,
}: {
  item: ToastItem;
  onRemove: (id: string) => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(item.id);
    }, item.duration || 4000);
    return () => clearTimeout(timer);
  }, [item, onRemove]);

  const icons: Record<ToastType, ReactNode> = {
    success: <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />,
    error: <XCircle className="w-5 h-5 text-rose-400 shrink-0" />,
    warning: <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />,
    info: <Info className="w-5 h-5 text-synaptix-cyan shrink-0" />,
  };

  const borderStyles: Record<ToastType, string> = {
    success: "border-emerald-500/30 bg-surface",
    error: "border-rose-500/30 bg-surface",
    warning: "border-amber-500/30 bg-surface",
    info: "border-synaptix-cyan/30 bg-surface",
  };

  return (
    <div
      role="alert"
      className={`pointer-events-auto flex items-start gap-3 p-4 rounded-xl border shadow-synaptix-card transition-all duration-200 animate-in fade-in slide-in-from-top-2 ${borderStyles[item.type]}`}
    >
      {icons[item.type]}
      <p className="text-sm font-medium text-main flex-1 leading-snug">
        {item.message}
      </p>
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        className="text-muted hover:text-main transition-colors -mr-1 -mt-1 p-1 rounded-md"
        aria-label="Cerrar notificación"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export interface ModalAlertProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  variant?: ToastType;
}

export const ModalAlert = ({
  isOpen,
  onClose,
  title,
  message,
  variant = "info",
}: ModalAlertProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "Tab") {
        if (buttonRef.current && document.activeElement !== buttonRef.current) {
          e.preventDefault();
          buttonRef.current.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    buttonRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const icons: Record<ToastType, ReactNode> = {
    success: <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />,
    error: <XCircle className="w-6 h-6 text-rose-400 shrink-0" />,
    warning: <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />,
    info: <Info className="w-6 h-6 text-synaptix-cyan shrink-0" />,
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
    >
      <div className="relative w-full max-w-md bg-surface border border-border rounded-xl p-6 shadow-synaptix-card space-y-4">
        <div className="flex items-center gap-3">
          {icons[variant]}
          <h3 className="text-lg font-grotesk font-semibold text-main">
            {title}
          </h3>
        </div>
        <p className="text-sm text-muted leading-relaxed">{message}</p>
        <div className="flex justify-end pt-2">
          <button
            ref={buttonRef}
            type="button"
            onClick={onClose}
            className="h-10 px-6 rounded-lg bg-synaptix-cyan text-synaptix-navy font-medium text-sm hover:bg-[#00cce6] transition-colors shadow-synaptix-glow focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};
